import type { TextProps } from '@chakra-ui/react';
import { Flex, Icon, Text } from '@chakra-ui/react';
import type { CardProps } from '@ui/components/Card';
import { Card } from '@ui/components/Card';
import Info from '@ui/components/Info';
import type { NftToken } from '@ui/components/NFTCard';
import { useTranslations } from '@ui/i18n';
import { CheckCircle } from '@veno-app/icons';
import { UnbondingStatus } from '@veno-app/sdk';
import { format } from 'date-fns';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';

export type BatchCardProps = CardProps & {
  nft: NftToken;
};

export const BatchCard: FC<BatchCardProps> = ({ nft, ...props }) => {
  const t = useTranslations();

  return (
    <Card mx="0" p="16px" {...props}>
      <Flex justifyContent="space-between">
        <Text textStyle="h4Bold">{'#' + nft.batchNo.toString()}</Text>
        <BatchStatus nft={nft} />
      </Flex>
      <Info
        title={t('Withdrawal date')}
        value={format(nft.unlockStartTime.toNumber() * 1000, 'dd/MM/yyyy')}
        mt="16px"
      />
      <Info
        title={t('Estimated claim date')}
        value={format(nft.unlockEndTime.toNumber() * 1000, 'dd/MM/yyyy')}
        mt="16px"
      />
    </Card>
  );
};

type BatchStatusProps = TextProps & {
  nft: NftToken;
};
const BatchStatus = ({ nft, ...props }: BatchStatusProps) => {
  const t = useTranslations();

  const { statusText, statusColor } = useMemo((): {
    statusText: ReactNode;
    statusColor: TextProps['color'];
  } => {
    switch (nft.unbondingStatus) {
      case UnbondingStatus.UNBONDED:
      case UnbondingStatus.UNBONDING:
        return {
          statusText: (
            <>
              {t('Completed')}{' '}
              <Icon
                as={CheckCircle}
                boxSize="24px"
                position="relative"
                top="6px"
              />
            </>
          ),
          statusColor: 'brand-green',
        };
      case UnbondingStatus.PROCESSING:
        return {
          statusText: t('Batching'),
          statusColor: 'brand-inactive',
        };
      case UnbondingStatus.PENDING_BOT:
      default:
        return {
          statusText: t('Queued'),
          statusColor: 'brand-pink',
        };
    }
  }, [nft.unbondingStatus, t]);

  return (
    <Text
      textStyle={{
        base: 'ctaCopy',
        desktop: 'bodyBold',
      }}
      color={statusColor}
      {...props}
    >
      {statusText}
    </Text>
  );
};
